/** XeAgents.ai**/
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import {
  BarChart3,
  CalendarCheck,
  Headphones,
  Mic,
  PhoneCall,
  UserCheck,
  type LucideIcon,
} from "lucide-react";

interface Capability {
  icon: LucideIcon;
  /** Key under tiles.* in the landing namespace. */
  key: string;
}

const CAPABILITIES: Capability[] = [
  { icon: Mic, key: "voiceAgents" },
  { icon: PhoneCall, key: "calls" },
  { icon: UserCheck, key: "sdr" },
  { icon: CalendarCheck, key: "booking" },
  { icon: Headphones, key: "support" },
  { icon: BarChart3, key: "analytics" },
];

/**
 * The six things the product does, directly under the hero - the answer to
 * "what is this" for someone who will not read a feature page.
 */
export function CapabilityTiles() {
  const { t } = useTranslation("landing");
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className="mx-auto max-w-7xl px-4 pb-4 pt-12 sm:px-6 lg:px-8" data-testid="capability-tiles">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-6">
        {CAPABILITIES.map(({ icon: Icon, key }, index) => (
          <motion.div
            key={key}
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: index * 0.05 }}
            className="rounded-2xl border border-slate-200 bg-white p-4 text-center transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
            data-testid={`capability-tile-${index}`}
          >
            <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-950/60">
              <Icon className="h-5 w-5 text-violet-600 dark:text-violet-400" />
            </span>
            <h3 className="mt-3 text-sm font-semibold leading-snug text-slate-900 dark:text-white">
              {t(`tiles.${key}.title`)}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              {t(`tiles.${key}.description`)}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default CapabilityTiles;
